import React, { FC } from 'react';
import { Divider, TPAComponentsProvider } from 'wix-ui-tpa';
import { useEnvironment, WidgetProps } from '@wix/yoshi-flow-editor';

import { ControllerProps, DataHook } from '../../../types';
import Section from './common/Section';
import Header from './Header';
import Description from './Description';
import Badges from './Badges';
import Overview from './Overview';
import BlogPosts from './BlogPosts';
import EmptyState from './EmptyState';
import {
  useBadgesRenderer,
  useBlockedEmptyStateRenderer,
  useBlogPostsRendered,
  useDescriptionRenderer,
  useEmptyStateRenderer,
  useOverviewRenderer,
} from './hooks';
import { classes, st } from './Widget.st.css';
import { ModalsContextProvider } from '../../../contexts/modals';
import { ToastContextProvider } from '../../../contexts/toast';
import { WidgetContextProvider } from '../../../contexts/widgets';

const getInfoSectionContentState = (
  shouldDisplayOverviewAsSidebar: boolean,
  shouldRenderOverview: boolean,
) => ({
  fullWidth: !shouldDisplayOverviewAsSidebar || !shouldRenderOverview,
});

const getInfoSectionSidebarState = (
  shouldDisplayOverviewAsSidebar: boolean,
  shouldRenderInfoSectionContent: boolean,
) => ({
  fullWidth: !shouldRenderInfoSectionContent || !shouldDisplayOverviewAsSidebar,
});

const Widget: FC<WidgetProps<ControllerProps>> = ({ host }) => {
  const { shouldRenderDescription } = useDescriptionRenderer();
  const { shouldRenderBadges } = useBadgesRenderer();
  const { shouldRenderOverview } = useOverviewRenderer();
  const { shouldRenderBlogPosts } = useBlogPostsRendered();
  const { shouldRenderEmptyState } = useEmptyStateRenderer({
    shouldRenderDescription,
    shouldRenderBadges,
    shouldRenderOverview,
    shouldRenderBlogPosts,
  });
  const { shouldRenderBlockedEmptyState } = useBlockedEmptyStateRenderer();
  const { isMobile, isRTL, isEditorX } = useEnvironment();

  const shouldRenderInfoSectionContent =
    shouldRenderDescription || shouldRenderBadges;

  const shouldDisplayOverviewAsSidebar = host.dimensions.width
    ? host.dimensions.width > 750
    : true;

  return (
    <div
      className={st(classes.root, {
        mobile: isMobile,
      })}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-hook={DataHook.WidgetWrapper}
    >
      <Header />
      {(!isMobile ||
        shouldRenderBlockedEmptyState ||
        shouldRenderEmptyState) && <Divider className={classes.divider} />}
      {shouldRenderBlockedEmptyState || shouldRenderEmptyState ? (
        <EmptyState isBlocked={shouldRenderBlockedEmptyState} />
      ) : (
        <>
          <div
            className={st(classes.infoSectionsWrapper, {
              childrenFullWidth: !shouldDisplayOverviewAsSidebar,
              responsive: isEditorX,
            })}
          >
            {shouldRenderInfoSectionContent && (
              <div
                className={st(
                  classes.infoSectionContent,
                  getInfoSectionContentState(
                    shouldDisplayOverviewAsSidebar,
                    shouldRenderOverview,
                  ),
                )}
              >
                {shouldRenderDescription && (
                  <Section>
                    <Description />
                  </Section>
                )}
                {shouldRenderBadges && (
                  <Section>
                    <Badges />
                  </Section>
                )}
              </div>
            )}
            {shouldRenderOverview && (
              <Section
                className={st(
                  classes.infoSectionSidebar,
                  getInfoSectionSidebarState(
                    shouldDisplayOverviewAsSidebar,
                    shouldRenderInfoSectionContent,
                  ),
                )}
              >
                <Overview />
              </Section>
            )}
          </div>
          {shouldRenderBlogPosts && (
            <Section>
              <BlogPosts />
            </Section>
          )}
        </>
      )}
    </div>
  );
};

const WidgetWrapper: FC<WidgetProps<ControllerProps>> = (props) => {
  const { isMobile, isRTL } = useEnvironment();

  if (!props.member?.id) {
    return null;
  }

  return (
    <WidgetContextProvider {...props}>
      <TPAComponentsProvider value={{ mobile: isMobile, rtl: isRTL }}>
        <ModalsContextProvider>
          <ToastContextProvider>
            <Widget {...props} />
          </ToastContextProvider>
        </ModalsContextProvider>
      </TPAComponentsProvider>
    </WidgetContextProvider>
  );
};

export default WidgetWrapper;
